/**
 * Narrative trigger engine. Tracks consumed species, categories, and
 * counts. Produces ordered narrator lines when thresholds hit. Pure
 * logic — the scene schedules the output through the HUD.
 *
 * Three trigger kinds:
 *   - firstConsumeLines[species] — fires once, first time a species
 *     is consumed
 *   - firstCategoryLines[category] — fires once, first time anything
 *     in a category is consumed (e.g. 'skeletal', 'exoskeletal')
 *   - countLines[species] — array of { at, line } thresholds fired
 *     when running count reaches `at`
 */
export interface CountLine {
  at: number;
  line: string;
}

export interface NarrativeConfig {
  firstConsumeLines: Record<string, string>;
  firstCategoryLines: Record<string, string>;
  countLines: Record<string, CountLine[]>;
}

export interface NarrativeSnapshot {
  consumedSpecies: string[];
  consumedCategories: string[];
  counts: Record<string, number>;
}

export class NarrativeDirector {
  private consumedSpecies = new Set<string>();
  private consumedCategories = new Set<string>();
  private counts = new Map<string, number>();

  constructor(private readonly config: NarrativeConfig) {}

  /**
   * Record a consume event. Returns the narrator lines that should
   * fire as a result, in priority order (species-specific first,
   * then category, then count thresholds).
   */
  processConsume(speciesId: string, categories: readonly string[] = []): string[] {
    const out: string[] = [];

    if (!this.consumedSpecies.has(speciesId)) {
      this.consumedSpecies.add(speciesId);
      const line = this.config.firstConsumeLines[speciesId];
      if (line) out.push(line);
    }

    for (const cat of categories) {
      if (this.consumedCategories.has(cat)) continue;
      this.consumedCategories.add(cat);
      const line = this.config.firstCategoryLines[cat];
      if (line) out.push(line);
    }

    const next = (this.counts.get(speciesId) ?? 0) + 1;
    this.counts.set(speciesId, next);
    const ladder = this.config.countLines[speciesId];
    if (ladder) {
      const hit = ladder.find((c) => c.at === next);
      if (hit) out.push(hit.line);
    }

    return out;
  }

  getCount(speciesId: string): number {
    return this.counts.get(speciesId) ?? 0;
  }

  hasConsumed(speciesId: string): boolean {
    return this.consumedSpecies.has(speciesId);
  }

  hasConsumedCategory(category: string): boolean {
    return this.consumedCategories.has(category);
  }

  snapshot(): NarrativeSnapshot {
    return {
      consumedSpecies: Array.from(this.consumedSpecies),
      consumedCategories: Array.from(this.consumedCategories),
      counts: Object.fromEntries(this.counts),
    };
  }

  restore(snap: NarrativeSnapshot): void {
    this.consumedSpecies = new Set(snap.consumedSpecies);
    this.consumedCategories = new Set(snap.consumedCategories);
    this.counts = new Map(Object.entries(snap.counts));
  }
}
